import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getPayees } from '../services/api';
import { useToken } from '../hooks/useToken';
import { useCurrentUser, isOnyxFC } from '../context/UserContext';
import type { Payee } from '../types';

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-indigo-500',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (const c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// 3-dot actions menu
function ActionsMenu({ payeeId, canEdit, onEdit }: {
  payeeId: number;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function close(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
      >
        ···
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-10 bg-white border border-gray-200 rounded-lg shadow-lg w-36 py-1">
          <button
            onClick={() => { navigate(`/requests?payee_id=${payeeId}`); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            View Requests
          </button>
          {canEdit && (
            <button
              onClick={() => { onEdit(); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 25;

export default function PayeesList() {
  const getToken = useToken();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const onyxFC = isOnyxFC(user);

  const [allPayees, setAllPayees] = useState<Payee[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(val: string) {
    setSearch(val);
    setPage(0);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => fetchPayees(val), 350);
  }

  async function fetchPayees(q?: string) {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await getPayees(token, q);
      setAllPayees(res.data);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchPayees(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(allPayees.length / PAGE_SIZE);
  const payees = allPayees.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Layout pageTitle="Payees">
      <p className="text-sm text-gray-500 mb-5">Active payees and contractors</p>

      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3 flex-1">
          {/* Summary */}
          <div className="text-sm text-gray-600">
            <strong className="text-gray-900">{total}</strong> payees
          </div>
          {/* Search */}
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search payees..."
            className="flex-1 max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {onyxFC && (
          <button
            onClick={() => navigate('/payees/new')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + Add New Payee
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        {error ? (
          <div className="px-5 py-8 text-center text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Loading...</div>
        ) : payees.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">No payees found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Payee Name</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Contact Person</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Contract</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {payees.map((p) => (
                <tr key={p.payee_id} className="border-b border-gray-50 hover:bg-gray-50 last:border-0">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${avatarColor(p.payee_name)}`}
                      >
                        {initials(p.payee_name)}
                      </span>
                      <span className="font-medium text-gray-900">{p.payee_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{p.email ?? '—'}</td>
                  <td className="px-5 py-3 text-gray-600">{p.email ?? '—'}</td>
                  <td className="px-5 py-3 text-gray-600">{p.phone ?? '—'}</td>
                  <td className="px-5 py-3">
                    {p.contract_required ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.contract_status === 'Signed'
                          ? 'bg-green-100 text-green-700'
                          : p.contract_status === 'Pending Signature'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {p.contract_status ?? 'Required'}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <ActionsMenu
                      payeeId={p.payee_id}
                      canEdit={onyxFC}
                      onEdit={() => navigate(`/payees/${p.payee_id}/edit`, { state: { payee: p } })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, allPayees.length)} of {allPayees.length}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-xs text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
